import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "select", "toggle"]
  static values = {
    options: Array,
    selected: String,
    open: Boolean
  }

  connect() {
    this.openValue = false
    this.highlightedIndex = -1

    if (this.selectedValue) {
      const selectedOption = this.optionsValue.find(o => String(o[1]) === this.selectedValue)
      if (selectedOption) {
        this.inputTarget.value = selectedOption[0]
      }
    }
  }

  open() {
    if (!this.openValue) {
      this.openValue = true
      this.renderOptions()
    }
  }

  close() {
    this.openValue = false
  }

  toggle() {
    this.openValue = !this.openValue
    if (this.openValue) {
      this.renderOptions()
    }
  }

  filter() {
    this.openValue = true
    this.highlightedIndex = -1
    this.renderOptions()
  }

  navigate(event) {
    if (!this.openValue) {
      this.renderOptions()
      this.openValue = true
    }

    const items = this.dropdownTarget.querySelectorAll("[data-combobox-option]")

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.highlightedIndex = Math.min(this.highlightedIndex + 1, items.length - 1)
      this.updateHighlight(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
      this.updateHighlight(items)
    } else if (event.key === "Enter") {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < items.length) {
        event.preventDefault()
        this.selectOptionElement(items[this.highlightedIndex])
      }
    } else if (event.key === "Escape") {
      this.openValue = false
    }
  }

  selectOption(event) {
    event.preventDefault()
    this.selectOptionElement(event.currentTarget)
  }

  selectOptionElement(element) {
    const value = element.dataset.value
    const label = element.dataset.label

    this.inputTarget.value = label
    this.selectTarget.value = value

    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.openValue = false
  }

  updateHighlight(items) {
    items.forEach((item, index) => {
      if (index === this.highlightedIndex) {
        item.classList.add("bg-slate-600")
      } else {
        item.classList.remove("bg-slate-600")
      }
    })
  }

  renderOptions() {
    const query = this.inputTarget.value.toLowerCase()
    const currentValue = this.selectTarget.value
    const filtered = this.optionsValue.filter(([label]) =>
      label.toLowerCase().includes(query)
    )

    this.dropdownTarget.innerHTML = filtered
      .map(([label, value]) => {
        const isSelected = String(value) === currentValue
        const highlightedLabel = this.highlightText(label, query)
        return `<li data-combobox-option data-value="${value ?? ""}" data-label="${this.escapeAttr(label)}" data-action="mousedown->combobox#selectOption" class="px-4 py-2 cursor-pointer hover:bg-slate-600 text-sm ${isSelected ? "text-blue-400" : "text-slate-300"}">${highlightedLabel}</li>`
      })
      .join("")

    if (filtered.length === 0) {
      this.dropdownTarget.innerHTML =
        '<li class="px-4 py-2 text-slate-500 text-sm">No results</li>'
    }
  }

  highlightText(text, query) {
    if (!query) return this.escapeHtml(text)

    const lowerText = text.toLowerCase()
    let result = ""
    let lastIndex = 0

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const regex = new RegExp(escapedQuery, "gi")
    let match

    while ((match = regex.exec(lowerText)) !== null) {
      result += this.escapeHtml(text.slice(lastIndex, match.index))
      result += `<mark class="bg-yellow-500 text-black rounded px-0.5">${this.escapeHtml(text.slice(match.index, match.index + query.length))}</mark>`
      lastIndex = match.index + query.length
    }

    result += this.escapeHtml(text.slice(lastIndex))

    return result
  }

  handleFocusOut(event) {
    setTimeout(() => {
      if (!this.element.contains(document.activeElement)) {
        this.openValue = false
      }
    }, 150)
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.openValue = false
    }
  }

  openValueChanged() {
    if (this.openValue) {
      this.dropdownTarget.classList.remove("hidden")
      document.addEventListener("click", this._boundDocumentClick)
    } else {
      this.dropdownTarget.classList.add("hidden")
      this.highlightedIndex = -1
      document.removeEventListener("click", this._boundDocumentClick)

      this.resetInput()
    }
  }

  resetInput() {
    if (!this.hasSelectTarget) return

    const currentValue = this.selectTarget.value
    if (currentValue !== "") {
      const selected = this.optionsValue.find(
        (o) => String(o[1]) === currentValue
      )
      this.inputTarget.value = selected ? selected[0] : ""
    } else {
      this.inputTarget.value = ""
    }
  }

  escapeHtml(text) {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }

  escapeAttr(text) {
    return text.replace(/&/g, "&amp;").replace(/"/g, "&quot;")
  }

  get _boundDocumentClick() {
    if (!this._documentClickBound) {
      this._documentClickBound = this.handleDocumentClick.bind(this)
    }
    return this._documentClickBound
  }

  disconnect() {
    document.removeEventListener("click", this._boundDocumentClick)
  }
}
